import { Button, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { queryLegalPerson } from '~/api';
import { LegalPerson } from '~/api/types';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import { formatCpfCnpj } from '~/helpers/formatters';
import { isValidCpfOrCnpj } from '~/helpers/validators';
import { LoadingScreen } from './loading';
import { isAxiosError } from 'axios';
import config from '~/config';

export const LegalPersonSelectionScreen: FC<Props> = ({ onLegalPersonSelection }) => {
  const [reqStatus, setReqStatus] = useState<ReqStatus>('idle');
  const [doc, setDoc] = useState('');
  const isValidDocument = isValidCpfOrCnpj(doc);
  const reqErrorMessage = isReqError(reqStatus) ? getErrorMessageFromReqStatus(reqStatus) : '';

  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  useEffect(() => {
    const preFilledDoc = new URLSearchParams(window.location.search).get('mainDocument');
    if (preFilledDoc) setDoc(preFilledDoc);
  }, []);

  const queryLegalPersonByDoc = async () => {
    if (!isValidDocument) return setReqStatus('invalid-document');
    try {
      setReqStatus('in-progress');
      const res = await queryLegalPerson(doc);

      const filteredDevelopments = res.legalPerson.developments.filter((d) => {
        // always show all contracts for non-white-label deployments
        if (!IS_WHITE_LABEL) return true;

        // show only contracts allowed of current magic link white-label
        const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
        if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

        return false;
      });

      if (filteredDevelopments.length === 0) return setReqStatus('not-found');
      else return onLegalPersonSelection(res.legalPerson);
    } catch (error: unknown) {
      console.error(error);
      if (isAxiosError(error) && error.response?.status === 429) {
        setReqStatus('too-many-requests');
      } else {
        setReqStatus('unknown-error');
      }
    }
  };

  if (reqStatus === 'in-progress') {
    return <LoadingScreen message="Buscando empreendimentos..." />;
  }

  return (
    <Page>
      <Title>Solicitar acesso</Title>
      <Body1>
        Por favor, digite seu CPF ou CNPJ registrado no contrato para solicitar acesso a sua Área do Cliente
      </Body1>
      <TextField
        label="Seu CPF ou CNPJ"
        value={formatCpfCnpj(doc)}
        onChange={(e) => setDoc(e.target.value.replace(/\D/g, '').substring(0, 14))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') queryLegalPersonByDoc();
        }}
        error={isReqError(reqStatus)}
        helperText={reqErrorMessage}
        autoFocus
        fullWidth
        variant="filled"
        inputProps={{ inputMode: 'numeric' }}
        sx={{ mb: 3, mt: 1 }}
      />
      <Button
        disabled={!isValidDocument}
        onClick={queryLegalPersonByDoc}
        fullWidth
        variant="contained"
        sx={{ height: '50px', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}
      >
        Solicitar acesso
      </Button>
    </Page>
  );
};

function getErrorMessageFromReqStatus(reqStatus: ReqError): string {
  const errorMap: Record<ReqError, string> = {
    'invalid-document': 'CPF/CNPJ inválido',
    'not-found': 'Não há contratos vinculados com este documento',
    'unknown-error': 'Erro desconhecido. Tente novamente.',
    'too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
  };
  return errorMap[reqStatus];
}

type ReqStatus = 'idle' | 'in-progress' | ReqError;
type ReqError = 'invalid-document' | 'not-found' | 'too-many-requests' | 'unknown-error';
function isReqError(e: unknown): e is ReqError {
  return ['invalid-document', 'not-found', 'too-many-requests', 'unknown-error'].includes(e as string);
}

interface Props {
  onLegalPersonSelection: (legalPerson: LegalPerson) => void;
}
